import { applyDesignerCommand, STICKER_DEFAULTS, type Color, type DesignAreaAnswer, type DesignAreaConfig, type StickerSettings } from '@pz/scene';
import { disclosure, el, note, segmented, switchRow, type DesignerCtx } from './panels.js';
import { createStepper } from './controls.js';
import { run } from './commands.js';
import { problemMessage } from './texts.js';
import { contrastRatio, hexToRgb, readableInk } from './contrast.js';

/**
 * "Nalepnica" — the contour die-cut, which the reference editor has no
 * equivalent of at all.
 *
 * Switching it on lays a white backing under everything in the zone, welds the
 * separate pieces together and hands the press one cut path. By default the
 * backing is SNUG: a thin rim that closes the gaps between letters only as far
 * as it takes to make one piece — a name comes out as one tidy sticker, not
 * letters in a thick halo. "Ravnomeran" is the old even border for shoppers
 * who want a wide white edge. Bridge width is an expert knob and lives under
 * "Napredno".
 *
 * The legend underneath is not decoration. A shopper cannot see a cut line in
 * their head, so the panel says in words what the dashed line is, that it does
 * not print, and — through `ctx.problems()` — whether the shape it produced is
 * one sticker or several.
 */

const STICKER_PROBLEM_CODES = ['sticker-edge', 'sticker-islands', 'sticker-empty'];
const advancedOpen = new WeakSet<DesignerCtx>();

/**
 * What a NEW sticker starts with on this product. The shop's own defaults
 * win; a product configured before the snug outline existed (no `outline`)
 * gets the snug outline with a rim no thicker than ours — that is the whole
 * point of it. Saved designs keep whatever they were stored with.
 */
export function shopperStickerSettings(config: DesignAreaConfig): StickerSettings {
  const defaults = config.sticker?.defaults;
  if (!defaults) return { ...STICKER_DEFAULTS };
  if (defaults.outline) return { ...defaults, imageBorder: defaults.imageBorder ?? false };
  return { ...defaults, imageBorder: defaults.imageBorder ?? false, outline: 'snug', borderMm: Math.min(defaults.borderMm, STICKER_DEFAULTS.borderMm) };
}

/**
 * Words whose colour no longer shows once the surface behind them changes —
 * white on a navy shirt becomes white on white the moment Sticker lays its
 * backing down — take readable ink instead. Chosen colours that still read
 * are left alone.
 */
export function reinkForSurface(answer: DesignAreaAnswer, surface: [number, number, number] | undefined, palette: readonly Color[]): DesignAreaAnswer {
  if (!surface) return answer;
  const ink = readableInk(surface, palette) ?? palette[0];
  if (!ink) return answer;
  let changed = false;
  const layers = answer.layers.map((layer) => {
    if (layer.kind !== 'text') return layer;
    const rgb = hexToRgb(layer.color.hex);
    if (!rgb || contrastRatio(rgb, surface) >= 1.8) return layer;
    changed = true;
    return { ...layer, color: { ...ink } };
  });
  return changed ? { ...answer, layers } : answer;
}

const WHITE: [number, number, number] = [255, 255, 255];

export function renderStickerPanel(body: HTMLElement, ctx: DesignerCtx): void {
  const settings = ctx.store.value().sticker;
  const required = ctx.rawConfig.sticker?.required === true;
  // The phone shows this panel in a sheet that `refreshPanel` does not
  // rebuild, so every change repaints the panel itself.
  const repaint = (): void => {
    if (!body.isConnected) return;
    body.textContent = '';
    renderStickerPanel(body, ctx);
  };
  const change = (next: StickerSettings): void => {
    if (run(ctx, { op: 'sticker', settings: next })) repaint();
  };

  body.append(note(settings?.imageBorder === true ? 'Beli obod i mostovi povezuju sve elemente u jedan komad, uključujući obod fotografije koji je podešen za ovaj proizvod.' : ctx.texts.stickerNote));

  const toggle = switchRow(ctx.texts.stickerOn, Boolean(settings), (on) => {
    try {
      // One step: the backing and any word that would vanish on it.
      const next = applyDesignerCommand(ctx.rawConfig, ctx.store.value(), { op: 'sticker', settings: on ? shopperStickerSettings(ctx.rawConfig) : null });
      ctx.dispatch({ type: 'set', answer: reinkForSurface(next, on ? WHITE : ctx.productRgb?.(), ctx.config.text.palette), select: ctx.selected()?.id ?? null });
      repaint();
    } catch (error) {
      ctx.toast(error instanceof Error ? error.message : 'Izmena nije moguća', 'error');
    }
  });
  if (required) {
    toggle.disabled = true;
    body.append(toggle, note(ctx.texts.stickerRequired, 'warn'));
  } else {
    body.append(toggle);
  }

  if (!settings) return;
  const snug = settings.outline === 'snug';

  body.append(el('strong', 'pzd-lbl', ctx.texts.stickerOutline));
  body.append(segmented([
    { id: 'snug', label: ctx.texts.stickerSnug, on: snug },
    { id: 'even', label: ctx.texts.stickerEven, on: !snug },
  ], (id) => change({ ...settings, outline: id === 'snug' ? 'snug' : 'even' })));
  body.append(el('p', 'pzd-hint', snug ? ctx.texts.stickerSnugHint : ctx.texts.stickerEvenHint));

  body.append(createStepper({
    label: snug ? ctx.texts.stickerRim : ctx.texts.stickerBorder,
    value: settings.borderMm,
    min: 0.5,
    max: 10,
    step: 0.5,
    unit: 'mm',
    slider: true,
    onCommit: (borderMm) => change({ ...settings, borderMm }),
  }).element);

  // The disclosure reports a wish; the panel flips it in place (a phone sheet
  // is not rebuilt by `refreshPanel`) and remembers it across repaints.
  const advanced = disclosure(ctx.texts.stickerAdvanced, advancedOpen.has(ctx), () => {
    const open = !advancedOpen.has(ctx);
    if (open) advancedOpen.add(ctx); else advancedOpen.delete(ctx);
    advanced.body.hidden = !open;
    advanced.wrap.classList.toggle('is-open', open);
    advanced.head.setAttribute('aria-expanded', String(open));
  });
  advanced.body.append(createStepper({
    label: ctx.texts.stickerBridge,
    value: settings.bridgeMm,
    min: 1,
    max: 10,
    step: 0.5,
    unit: 'mm',
    slider: true,
    onCommit: (bridgeMm) => change({ ...settings, bridgeMm }),
  }).element);
  body.append(advanced.wrap);

  // Whatever the contour check reported for this design, in the panel that
  // caused it — so the shopper reads it while the controls are in reach.
  // `problemMessage`, never `problem.detail`: the detail is the platform's
  // English diagnostic for a log, not a sentence for a shopper.
  const problems = ctx.problems().filter((problem) => STICKER_PROBLEM_CODES.includes(problem.code));
  for (const problem of problems) {
    body.append(note(problemMessage(problem), 'bad'));
  }

  const legend = el('div', 'pzd-legend');
  const line = el('span', 'pzd-legend-line');
  legend.append(line, el('span', undefined, ctx.texts.stickerCutNote));
  body.append(legend);
}
